/**
 * Environment-based configuration for Trackpac V2 UI
 * This replaces the hardcoded config.js approach with secure environment variables
 */

import { logger } from "./utils/logger";

// Helper function to get environment variable with fallback
const getEnvVar = (key, fallback = "") => {
  return import.meta.env[key] || fallback;
};

// Helper function to get boolean environment variable
const getEnvBool = (key, fallback = false) => {
  const value = import.meta.env[key];
  if (value === undefined) return fallback;
  return value === "true" || value === "1";
};

// Helper function to get number environment variable
const getEnvNumber = (key, fallback = 0) => {
  const value = import.meta.env[key];
  if (value === undefined) return fallback;
  const parsed = Number(value);
  return isNaN(parsed) ? fallback : parsed;
};

export const CONFIG = {
  // API Configuration
  API_URL: getEnvVar("VITE_API_URL", "https://v2-api.trackpac.io"),
  API_VERSION: getEnvVar("VITE_API_VERSION", "v1"),

  // Authentication (Auth0)
  Auth0Domain: getEnvVar("VITE_AUTH0_DOMAIN"),
  Auth0ClientId: getEnvVar("VITE_AUTH0_CLIENT_ID"),
  Auth0Audience: getEnvVar("VITE_AUTH0_AUDIENCE"),

  // Mapbox Configuration
  mapboxAccessToken: getEnvVar("VITE_MAPBOX_ACCESS_TOKEN"),
  mapStyle: getEnvVar(
    "VITE_MAPBOX_STYLE",
    "mapbox://styles/bfgneil/cle2sqoh8003s01ogafzo3xsl"
  ),

  // Branding & UI
  siteTitle: getEnvVar("VITE_SITE_TITLE", "Trackpac"),
  siteTitleDescription: getEnvVar(
    "VITE_SITE_TITLE_DESCRIPTION",
    "IoT Sensor Management Made Easy!"
  ),
  websiteURL: getEnvVar("VITE_WEBSITE_URL", "https://v2.trackpac.io"),
  shareUrl: getEnvVar("VITE_SHARE_URL", "https://v2.trackpac.io/share/"),
  dashboardShareUrl: getEnvVar(
    "VITE_DASHBOARD_SHARE_URL",
    "https://v2.trackpac.io/share/dashboard/"
  ),

  // Theme Configuration
  primaryColour: getEnvVar("VITE_PRIMARY_COLOUR", "#00D09F"),
  primaryColourRGB: {
    r: getEnvNumber("VITE_PRIMARY_COLOUR_R", 0),
    g: getEnvNumber("VITE_PRIMARY_COLOUR_G", 208),
    b: getEnvNumber("VITE_PRIMARY_COLOUR_B", 159),
  },
  secondaryColour: getEnvVar("VITE_SECONDARY_COLOUR", "#004d40"),
  defaultTheme: getEnvVar("VITE_DEFAULT_THEME", "light"),
  defaultLanguage: getEnvVar("VITE_DEFAULT_LANGUAGE", "en"),
  locale: getEnvVar("VITE_LOCALE", "en"),
  defaultDateFormat: getEnvVar("VITE_DEFAULT_DATE_FORMAT", "DD/MM/YYYY"),

  // Hardware Configuration
  battery_minMV: getEnvNumber("VITE_BATTERY_MIN_MV", 2700),
  battery_maxMV: getEnvNumber("VITE_BATTERY_MAX_MV", 3600),

  // Feature Flags
  organisation_enabled: getEnvBool("VITE_ORGANISATION_ENABLED", true),
  billing_enabled: getEnvBool("VITE_BILLING_ENABLED", false),
  manual_add: getEnvBool("VITE_MANUAL_ADD", false),
  self_host: getEnvBool("VITE_SELF_HOST", false),
  feedback_enabled: getEnvBool("VITE_FEEDBACK_ENABLED", false),
  show_geofences: getEnvBool("VITE_SHOW_GEOFENCES", true),
  api_key_enabled: getEnvBool("VITE_API_KEY_ENABLED", false),
  register_card_enabled: getEnvBool("VITE_REGISTER_CARD_ENABLED", false),
  show_buy_link: getEnvBool("VITE_SHOW_BUY_LINK", false),
  show_support_link: getEnvBool("VITE_SHOW_SUPPORT_LINK", false),
  trip_registry_enabled: getEnvBool("VITE_TRIP_REGISTRY_ENABLED", false),
  trip_registry_url: getEnvVar(
    "VITE_TRIP_REGISTRY_URL",
    "https://trip-registry.connect-iot.eu"
  ),

  // External Links
  feedback_url: getEnvVar(
    "VITE_FEEDBACK_URL",
    "https://trackpac.canny.io/feature-device-requests"
  ),
  buy_link: getEnvVar(
    "VITE_BUY_LINK",
    "https://hexaspot.com/collections/trackpac"
  ),
  support_url: getEnvVar(
    "VITE_SUPPORT_URL",
    "https://docs.trackpac.io/docs/introduction"
  ),
};

// Validation function to check required environment variables
export const validateConfig = () => {
  const requiredVars = [
    "VITE_AUTH0_DOMAIN",
    "VITE_AUTH0_CLIENT_ID",
    "VITE_AUTH0_AUDIENCE",
    "VITE_MAPBOX_ACCESS_TOKEN",
  ];

  const missing = requiredVars.filter((varName) => !import.meta.env[varName]);

  if (missing.length > 0) {
    logger.error("Missing required environment variables:", missing);
    logger.error(
      "Please check your .env file and ensure all required variables are set."
    );

    // In development, show helpful error
    if (import.meta.env.DEV) {
      const errorMessage = `
Missing required environment variables:
${missing.map((var_) => `- ${var_}`).join("\n")}

Please create a .env.local file with these variables.
See .env.example for reference.
      `;
      throw new Error(errorMessage);
    }
  }

  return missing.length === 0;
};
